import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import {
	BBBackendQueryOptions,
	type PaymentDetails,
	type CheckoutSession,
} from "@bidbattles/bloks/lib/rapace";
import { toast } from "sonner";
import { Result } from "@bidbattles/bloks/lib/result";
import type { LinkProps } from "@tanstack/react-router";

const broadcastChannel = new BroadcastChannel("bb:payments");

export type UsePaymentsResult = ReturnType<typeof usePayments>;
export function usePayments() {
	const [id, setId] = useState<string | null>(null);
	const paymentQuery = useQuery({
		...BBBackendQueryOptions.fetchPaymentDetails({
			id: id ?? "",
		}),
		enabled: !!id,
		refetchInterval: 1000,
		refetchIntervalInBackground: true,
	});
	const paymentRef = useRef<{
		toastId: string | number;
		session: CheckoutSession;
		resolve: (value: Result<PaymentDetails>) => void;
		cleanup: () => void;
		checkoutWin: Window | null;
	} | null>(null);

	const initiateMutation = useMutation({
		mutationFn: async (
			fn: (params: { redirect_url: string }) => Promise<
				Result<CheckoutSession>
			>,
		): Promise<Result<PaymentDetails>> => {
			const cleanup = () => {
				window.removeEventListener("beforeunload", listener);
				toast.dismiss(toastId);
				window.focus();
				checkoutWin?.close();

				if (paymentRef.current?.toastId === toastId) {
					paymentRef.current = null;
				}
			};

			const route = "/callbacks/payment" as const satisfies LinkProps["to"];
			const redirectUrl = new URL(route, window.location.href);
			const result = await fn({
				redirect_url: redirectUrl.toString(),
			});
			if (!result.ok) {
				return result;
			}

			setId(result.data.payment_id);
			const listener = (event: BeforeUnloadEvent) => {
				event.preventDefault();
				event.returnValue = true;
				return true;
			};
			window.addEventListener("beforeunload", listener);

			const toastId = toast.loading(
				"Do not leave this page, waiting to verify your payment...",
			);

			let checkoutWin: Window | null = null;
			if (result.data.checkout_url) {
				checkoutWin = window.open(result.data.checkout_url, "_blank");
			}

			const paymentPromise = new Promise<Result<PaymentDetails>>((resolve) => {
				paymentRef.current = {
					toastId,
					session: result.data,
					resolve,
					cleanup,
					checkoutWin,
				};
			});

			paymentPromise.then(cleanup);

			return await paymentPromise;
		},
	});

	const payment = paymentQuery.data;
	const paymentStatus = payment?.status ?? "pending";
	let status: "pending" | "cancelled" | "failed" | "paid";
	switch (paymentStatus) {
		case undefined:
		case "pending":
		case "authorized":
			status = "pending";
			break;
		case "expired":
		case "canceled":
			status = "cancelled";
			break;
		case "failed":
			status = "failed";
			break;
		case "paid":
			status = "paid";
			break;
		default: {
			const _: never = paymentStatus;
			throw new Error(`Unexpected payment status: ${paymentStatus}`);
		}
	}

	useEffect(() => {
		return () => {
			if (paymentRef.current) {
				paymentRef.current.cleanup();
			}
		};
	}, []);

	useEffect(() => {
		if (status !== "pending" && payment) {
			switch (status) {
				case "paid":
					paymentRef.current?.resolve(Result.ok(payment));
					break;
				case "cancelled":
					paymentRef.current?.resolve(
						Result.error(new Error("Payment cancelled or expired")),
					);
					break;
				default:
					paymentRef.current?.resolve(
						Result.error(new Error("Payment unsuccessful")),
					);
			}
		}
	}, [status, payment]);

	useEffect(() => {
		const broadcastListener = () => {
			if (paymentRef.current) {
				window.focus();
				paymentRef.current.checkoutWin?.close();
			}
		};
		broadcastChannel.addEventListener("message", broadcastListener);
		return () => {
			broadcastChannel.removeEventListener("message", broadcastListener);
		};
	}, []);

	const notifyShouldGrabFocus = useCallback(() => {
		broadcastChannel.postMessage({ type: "focus" });
	}, []);

	return {
		status,
		payment,
		initiate: initiateMutation.mutateAsync,
		notifyShouldGrabFocus,
	};
}
